import Image from 'next/image';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { useGetPresignedUrlMutation, useGetUserQuery } from '../../../api';
import { Button } from '../../../ui';

export const Financials = ({
  capitalRequestInput,
  setCurrentStep,
  handleChange,
  isLoading,
}: {
  capitalRequestInput: CapitalRequestInput;
  handleChange: (
    key: string,
    value: { year: number; file: { fileName: string; s3Key: string } }[]
  ) => void;
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  isLoading: boolean;
}) => {
  const { data: user } = useGetUserQuery();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const fileRef = useRef<HTMLInputElement>(null);
  const currentYear = new Date().getFullYear();
  const yearsOptions = [currentYear, currentYear - 1, currentYear - 2].filter(
    (year) =>
      capitalRequestInput.financialStatements
        .flatMap((financialStatement) => financialStatement.year)
        .indexOf(year) === -1
  );
  const [year, setYear] = useState(yearsOptions[0]);

  useEffect(() => {
    if (yearsOptions.length > 0) {
      setYear(yearsOptions[0]);
    }
  }, [yearsOptions]);

  const handleUpload = async (file: File) => {
    const fileName = `financial_statements/${year}.${file.name.match(/\.([^\.]+)$/)?.[1] ?? ''}`;
    const presignedUrl = await getPresignedUrl({
      contentType: file.type,
      fileName,
    });
    if (presignedUrl.data?.presignedUrl) {
      await fetch(presignedUrl.data?.presignedUrl, {
        method: 'PUT',
        body: file,
      });
      capitalRequestInput.financialStatements.push({
        year: year || 0,
        file: {
          fileName: fileName.split('/').join('_'),
          s3Key: `${user?.orgId}/${fileName}`,
        },
      });
      handleChange(
        'financialStatements',
        capitalRequestInput.financialStatements
      );
      fileRef.current?.value && (fileRef.current.value = '');
    }
  };

  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold">Financial Statements</span>
          <span className="text-base font-normal">
            Please upload your financial statements of the last three years.
          </span>
          <div className="flex gap-2 flex-wrap">
            {capitalRequestInput.financialStatements.map(
              (financialStatement, index) => (
                <div
                  className="flex gap-2 p-2 rounded-xl bg-gray-100"
                  key={index}
                >
                  <Image
                    src={'/icons/file-gray.svg'}
                    width={20}
                    height={20}
                    alt=""
                  />
                  {`financial_statements_${financialStatement.year}.${financialStatement.file.fileName.match(/\.([^\.]+)$/)?.[1] ?? ''}`}
                  <Image
                    src={'/icons/trash-red.svg'}
                    width={20}
                    height={20}
                    alt=""
                    onClick={() =>
                      handleChange(
                        'financialStatements',
                        capitalRequestInput.financialStatements.filter(
                          (_, i) => i !== index
                        )
                      )
                    }
                  />
                </div>
              )
            )}
          </div>
          {capitalRequestInput.financialStatements.length < 3 && (
            <div className="flex gap-2">
              <div
                className="flex-grow rounded-2xl  border border-primary-50 bg-primary-25 p-2 flex gap-2 cursor-pointer"
                onClick={() => fileRef.current?.click()}
              >
                <Image
                  src={'/icons/upload.svg'}
                  width={20}
                  height={20}
                  alt=""
                />
                <span>Financial Statements</span>
              </div>
              <input
                type="file"
                className="hidden"
                ref={fileRef}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUpload(file);
                  }
                }}
              />
              <select
                className="bg-gray-100 rounded-2xl px-2"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearsOptions.map((year) => (
                  <option key={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('GROWTH PLAN')}
            />
          </div>
          <div>
            <Button
              text="Continue"
              variant={isLoading ? 'disabled' : 'primary'}
              suffixIcon="/icons/arrow_right.svg"
              onClick={() => setCurrentStep('OWNERSHIP')}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src="/create_cr.png"
          alt="Supplier Request Image"
          priority
          width={512}
          height={584}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
